import React from "react";
import {
  Alert,
  Breadcrumbs,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { helpPages } from "./pages";

const HELP_VERSION = process.env.REACT_APP_HELP_VERSION ?? "1";

const HelpDetail = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const normalizedSlug = React.useMemo(() => {
    if (!slug) return undefined;
    return slug.replace(/\.html$/i, "");
  }, [slug]);
  const helpSlugs = React.useMemo(
    () => new Set(helpPages.map((item) => item.slug)),
    []
  );
  const page = helpPages.find((item) => item.slug === normalizedSlug);

  const [content, setContent] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const assetUrl = React.useMemo(() => {
    if (!page) return "";
    const basePath =
      process.env.PUBLIC_URL && process.env.PUBLIC_URL !== "."
        ? process.env.PUBLIC_URL
        : "";
    const normalizedBase = basePath.replace(/\/$/, "");
    const cacheBuster = HELP_VERSION;
    return `${normalizedBase}/help/${page.fileName}?v=${cacheBuster}`;
  }, [page]);

  const loadHelp = React.useCallback(
    async (url: string, signal: AbortSignal) => {
      const fetchHtml = async (targetUrl: string, cache: RequestCache) => {
        const res = await fetch(targetUrl, { signal, cache });
        if (!res.ok) {
          throw new Error(`Unable to load help page: ${res.status}`);
        }
        const html = await res.text();
        if (!html.trim()) {
          throw new Error("Empty help page response");
        }
        return html;
      };

      try {
        // Use default caching first (allows 304 with cached body).
        return await fetchHtml(url, "default");
      } catch (err) {
        // Fallback: cache-bust to avoid stale/empty CDN responses.
        const bustParam = `ts=${Date.now()}`;
        const bustUrl = url.includes("?") ? `${url}&${bustParam}` : `${url}?${bustParam}`;
        return await fetchHtml(bustUrl, "no-store");
      }
    },
    []
  );

  React.useEffect(() => {
    if (!page || !assetUrl) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setContent("");

    loadHelp(assetUrl, controller.signal)
      .then((html) => setContent(html))
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Unable to load help page", err);
        setError("We couldn't load this guide right now. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [page, assetUrl, loadHelp]);

  const onContentClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href) return;

      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;

        const normalizedPath = url.pathname.replace(/\.html$/i, "");
        if (!normalizedPath.startsWith("/help/")) return;

        const rawSlug = normalizedPath.slice("/help/".length);
        const routeSlug = decodeURIComponent(rawSlug);
        if (!routeSlug || routeSlug.includes("/") || !helpSlugs.has(routeSlug)) {
          return;
        }

        event.preventDefault();
        navigate(`${normalizedPath}${url.search}${url.hash}`);
      } catch {
        // Ignore malformed URLs and allow native browser behavior.
      }
    },
    [helpSlugs, navigate]
  );

  if (!page) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Typography variant="h4" fontWeight={700}>
            Help centre
          </Typography>
          <Alert severity="warning">
            We couldn't find that help article.{" "}
            <Link component={RouterLink} to="/help" underline="hover">
              Go back to the help index
            </Link>
            .
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link component={RouterLink} color="inherit" to="/help" underline="hover">
            Help centre
          </Link>
          <Typography color="text.primary">{page.title}</Typography>
        </Breadcrumbs>
        <Typography variant="h4" fontWeight={700}>
          {page.title}
        </Typography>

        {loading && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            color="text.secondary"
            mt={1}
          >
            <CircularProgress size={20} />
            <Typography variant="body2">Loading guide…</Typography>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <Box
            onClick={onContentClick}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: theme.shadows[0],
              p: { xs: 2, md: 3 },
              "& .scribe-title": {
                fontSize: "1.75rem",
                fontWeight: 700,
                marginBottom: theme.spacing(1),
              },
              "& .scribe-author": {
                color: theme.palette.text.secondary,
                marginBottom: theme.spacing(2),
              },
              "& .scribe-description": {
                color: theme.palette.text.secondary,
                marginBottom: theme.spacing(3),
                lineHeight: 1.6,
              },
              "& .scribe-step": {
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                paddingLeft: theme.spacing(2),
                marginBottom: theme.spacing(3),
              },
              "& .scribe-step-text": {
                margin: 0,
                color: theme.palette.text.primary,
                lineHeight: 1.6,
              },
              "& .scribe-step-tip": {
                backgroundColor: theme.palette.action.hover,
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1.5),
                display: "inline-block",
              },
              "& .scribe-screenshot-container": {
                margin: `${theme.spacing(2)} 0`,
              },
              "& .scribe-screenshot": {
                width: "100%",
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[1],
                display: "block",
              },
              "& .scribe-footer": {
                color: theme.palette.text.secondary,
                marginTop: theme.spacing(3),
              },
              "& a": {
                color: theme.palette.primary.main,
                fontWeight: 600,
                wordBreak: "break-word",
              },
              "& h1, & h2, & h3, & h4": {
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(1),
                fontWeight: 700,
              },
              "& p": {
                color: theme.palette.text.primary,
              },
            })}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </Stack>
    </Container>
  );
};

export default HelpDetail;
